import type { RefObject } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Alert, AuthCode, Button, Heading, Link, Text } from '@ksyos/design-system';
import { createRef } from 'react';
import { connect } from 'react-redux';
import type { ILogin } from '@common/interfaces/login';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import type { ThirdPartySsoName } from '../../../../common/enums/thirdPartySsoName';
import type { ISessionUser } from '../../../configureStore';
import type { IConnectProps } from '../../../utils/connectProps';
import type { ValidationErrors } from '../../../utils/validation';
import type { IUser } from '../../interfaces/user';
import { LockStatus } from '../../../../common/enums/lockStatus';
import { TwoFactorAuthenticationType } from '../../../../common/enums/twoFactorAuthenticationType';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { getDisplayInitials } from '../../../../common/utils/getDisplayInitials';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import {
    noMfaTimeAmount,
    noMfaTimeUnit,
    practitionerHasMfaTemporarilyDisabled,
} from '../../../../common/utils/isMfaVerificationDisabled';
import { loginCredentialsSchema, loginFullSchema } from '../../../../common/validation/practitioner';
import { receiveLogin } from '../../../store/authentication/actions';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { timeOutInactivityModalMilliseconds, timeOutWarningMessageMilliseconds } from '../../../utils/timeoutConstants';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { AnimatedLock } from '../../elements/AnimatedLock';
import { Component } from '../../elements/Component';
import { Modal } from '../../elements/modals/Modal';
import { RequireMfa } from '../../elements/RequireMfa';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../customControls/utils/formAnalytics';
import { LoginCredentials, LoginTypes } from './LoginCredentials';
import { MfaSetupAtLogin } from './MfaSetupAtLogin';

interface IRememberedUser extends IUser {
    lastLogin: Date;
}

interface ILoginState {
    errorCode?: string;
    errors: ValidationErrors;
    fullName?: string;
    isFormDisabled: boolean;
    isLoginButtonDisabled: boolean;
    lockStatus: LockStatus;
    login: ILogin;
    mfaType: TwoFactorAuthenticationType | null;
    mfaVerificationSwitchedOffOn: string | Date | null;
    practitionerId: string | null;
    redirectUrl: string | null;
    remainingAttempts: number | null;
    rememberedUsers: IRememberedUser[];
    showInactivityModal: boolean;
    showWarningMessage: boolean;
    showWrongTokenWarningMessage: boolean;
    step: 'credentials' | 'totp' | 'mfaRequired' | 'temporarilyDisabledMfa' | 'mfaSetupAtLogin';
    user: ISessionUser | null;
}

class InnerNewLogin extends Component<IConnectProps & IWithRouterProps, ILoginState> {
    private warningMessageTimeout: number;
    private inactivityModalTimeout: number;
    private formRef: RefObject<HTMLFormElement>;
    private buttonRef: RefObject<HTMLButtonElement>;

    constructor(props: IConnectProps & IWithRouterProps) {
        super(props);

        const rememberedUsersJson = localStorage.getItem('rememberedUsers');
        let rememberedUsers: IRememberedUser[] = rememberedUsersJson ? JSON.parse(rememberedUsersJson) : [];

        rememberedUsers = this.removeUsersWithExpiredLastLoginDate(rememberedUsers);
        const firstUserUserName = rememberedUsers.length > 0 ? rememberedUsers[0].username : '';

        this.formRef = createRef();
        this.buttonRef = createRef();

        const searchParams = props.router.searchParams;

        this.state = {
            errors: emptyValidationErrors,
            isFormDisabled: false,
            isLoginButtonDisabled: false,
            lockStatus: LockStatus.default,
            login: {
                episodeCategory: searchParams.getOptional('episodeCategory'),
                issuer: searchParams.getOptional('issuer') as ThirdPartySsoName,
                organizationId: searchParams.getOptional('organizationId'),
                password: '',
                patientId: searchParams.getOptional('patientId'),
                requestId: searchParams.getOptional('requestId'),
                ssoToken: searchParams.getOptional('ssoToken'),
                totpToken: '',
                userName: searchParams.get('prefillUserName') ?? firstUserUserName,
            },
            mfaType: null,
            mfaVerificationSwitchedOffOn: null,
            practitionerId: null,
            redirectUrl: null,
            remainingAttempts: null,
            rememberedUsers,
            showInactivityModal: false,
            showWarningMessage: false,
            showWrongTokenWarningMessage: false,
            step: 'credentials',
            user: null,
        };
    }

    public async componentDidMount() {
        startView('login');
        const formNode = this.formRef.current;
        const buttonRef = this.buttonRef.current;
        addFormAnalytics(formNode, buttonRef);
    }

    public render() {
        const searchParams = this.props.router.searchParams;
        switch (this.state.step) {
            case 'credentials':
                return (
                    <LoginCredentials
                        buttonRef={this.buttonRef}
                        errorCode={this.state.errorCode}
                        errors={this.state.errors}
                        formRef={this.formRef}
                        handleChange={async (key, value) => await this.handleChange(key, value)}
                        handleChangeUserSelect={async (value) => await this.handleChangeUserSelect(value)}
                        isFormDisabled={this.state.isFormDisabled}
                        isLoginButtonDisabled={this.state.isLoginButtonDisabled}
                        login={this.state.login}
                        loginType={
                            !!this.state.login.issuer && !!this.state.login.ssoToken
                                ? LoginTypes.firstCSLogin
                                : LoginTypes.regularCSLogin
                        }
                        onDoValidate={async (name) => await this.validateOne(name)}
                        onSubmit={async () => await this.handleSubmitCredentials()}
                        remainingAttempts={this.state.remainingAttempts}
                        rememberedUsers={this.state.rememberedUsers}
                        userNamePrefilled={Boolean(searchParams.get('prefillUserName'))}
                    />
                );
            case 'totp':
                this.setWarningMessageTimeout();

                return (
                    <>
                        <Heading>Log in als zorgprofessional</Heading>
                        {this.state.showWrongTokenWarningMessage && this.getWrongTokenWarningMessage()}
                        {this.state.showWarningMessage && (
                            <Alert
                                appearance="warning"
                                noMargin={true}
                            >
                                {translateKey('login.totpTokenInactivity')}{' '}
                                <Alert.Link
                                    href="https://www.ksyos.nl/contact/faq"
                                    openInNewTab={true}
                                    variant="externalLink"
                                >
                                    {' '}
                                    Lees onze FAQ
                                </Alert.Link>
                            </Alert>
                        )}
                        <AnimatedLock color={this.state.lockStatus}></AnimatedLock>
                        <Text textAlign="center">
                            Vul de code uit je{' '}
                            {this.state.mfaType === TwoFactorAuthenticationType.verificationDevice
                                ? 'hardware token'
                                : 'verificatieapp'}{' '}
                            in
                        </Text>
                        <AuthCode
                            appearance={this.state.lockStatus}
                            length={6}
                            onChange={(token) => this.handleChangeToken(token)}
                        />
                        {this.state.showInactivityModal && this.showInactivityModal()}
                    </>
                );
            case 'temporarilyDisabledMfa':
                return (
                    <>
                        <Heading>Log in als zorgprofessional</Heading>
                        <Text>
                            Je logt tijdelijk in zonder tweestapsverificatie.
                            <br />
                            <br />
                            Op{' '}
                            <b>
                                {DateTime.from(this.state.mfaVerificationSwitchedOffOn ?? new Date())
                                    .add(noMfaTimeAmount, noMfaTimeUnit)
                                    .format('DD-MM-YYYY')}
                            </b>{' '}
                            zetten we de extra beveiliging aan. Stel voor deze datum tweestapsverificatie in, en voorkom
                            dat je niet kunt inloggen.
                            <br />
                            <br />
                            {'Instellen kan via het Persoonlijke menu > Profiel > Beveiliging.'}
                        </Text>
                        <Button
                            color="secondary"
                            onClick={() => {
                                this.props.dispatch(receiveLogin(this.state.user));
                                const startPage =
                                    this.state.redirectUrl ?? this.props.router.location?.pathname ?? '/home';
                                this.goToStartpage(startPage);
                            }}
                            width="full"
                        >
                            {translateKey('common.next')}
                        </Button>
                    </>
                );
            case 'mfaRequired':
                return <RequireMfa />;
            case 'mfaSetupAtLogin':
                return (
                    <MfaSetupAtLogin
                        password={this.state.login.password}
                        userName={this.state.login.userName}
                    />
                );
            default:
                return;
        }
    }

    private getWrongTokenWarningMessage() {
        return (
            <Alert
                appearance="warning"
                noMargin={true}
            >
                Je hebt een aantal keer een verkeerde code ingevuld. Hulp nodig?{' '}
                <Alert.Link
                    href="https://www.ksyos.nl/contact/faq"
                    openInNewTab={true}
                    variant="externalLink"
                >
                    {' '}
                    Lees onze FAQ
                </Alert.Link>
                <br />
                <br />
                {`Wanneer je nog ${this.state.remainingAttempts} keer een verkeerde code invult wordt je account geblokkeerd voor 15 minuten.`}
            </Alert>
        );
    }

    private async handleChange<K extends keyof ILogin>(name: K, value: ILogin[K]) {
        await this.setState(({ login }) => ({ login: { ...login, [name]: value } }));
    }

    private async validateOne(name: keyof ILogin) {
        await this.setState(({ login, errors }) => ({
            errors: updateValidationErrorsForOne(loginFullSchema, login, errors, name),
        }));
    }

    private async handleChangeUserSelect(value: string) {
        await this.setState((prevState) => ({
            errorCode: undefined,
            errors: emptyValidationErrors,
            isFormDisabled: false,
            login: { ...prevState.login, password: '', userName: value },
        }));
    }

    private async handleSubmitCredentials() {
        addTrackFormSubmit(this.buttonRef.current);

        const { errors, item } = validate(loginCredentialsSchema, this.state.login);

        await this.setState({ errors });

        if (errors.size > 0) {
            return;
        }

        try {
            // Prevent double firing of login calls while waiting for response
            await this.setState({ isLoginButtonDisabled: true });

            const result = await fetchJson('/api/checkCredentials', 'POST', item, [
                ValidationErrorCode.USERNAME_NOT_FOUND,
                ValidationErrorCode.USER_NOT_REGISTERED,
                ValidationErrorCode.WRONG_PASSWORD,
                ValidationErrorCode.WRONG_PASSWORD_WARNING,
                ValidationErrorCode.USER_BLOCKED_TEMPORARILY,
                ValidationErrorCode.USER_HAS_NO_EMPLOYMENT,
                ValidationErrorCode.USER_IS_NOT_ACTIVATED,
                ValidationErrorCode.MFA_REQUIRED_LOGIN_NOT_ALLOWED,
                ValidationErrorCode.MFA_SETUP_MANDATORY_LOGIN_NOT_ALLOWED,
                ValidationErrorCode.USER_INFORMATION_IS_CORRUPT,
            ]);

            if (result.status === 200) {
                addFormConversion(this.formRef.current);
            }
            await this.setState({ errorCode: undefined });

            if (result.errorCode === ValidationErrorCode.USERNAME_NOT_FOUND) {
                return await this.setState({ errors: emptyValidationErrors.set('userName', result.errorCode) });
            } else if (result.errorCode === ValidationErrorCode.WRONG_PASSWORD) {
                return await this.setState({ errors: emptyValidationErrors.set('password', result.errorCode) });
            } else if (result.errorCode === ValidationErrorCode.WRONG_PASSWORD_WARNING) {
                return await this.setState({
                    errorCode: result.errorCode,
                    errors: emptyValidationErrors.set('password', result.errorCode),
                    remainingAttempts: Number(result.message),
                });
            } else if (result.errorCode === ValidationErrorCode.USER_BLOCKED_TEMPORARILY) {
                return await this.setState({
                    errorCode: result.errorCode,
                    errors: emptyValidationErrors,
                    isFormDisabled: true,
                    login: { password: '', totpToken: '', userName: '' },
                });
            } else if (result.errorCode === ValidationErrorCode.MFA_REQUIRED_LOGIN_NOT_ALLOWED) {
                return await this.setState({ step: 'mfaRequired' });
            } else if (result.errorCode === ValidationErrorCode.MFA_SETUP_MANDATORY_LOGIN_NOT_ALLOWED) {
                return await this.setState({ step: 'mfaSetupAtLogin' });
            } else if (result.errorCode) {
                return await this.setState({ errorCode: result.errorCode });
            }

            const practitionerHasMfaDeactivated = practitionerHasMfaTemporarilyDisabled(
                result.json.mfaVerificationSwitchedOffOn
            );
            if (
                result.json.twoFactorAuthenticationType &&
                result.json.twoFactorAuthenticationType !== TwoFactorAuthenticationType.temporarilyDisabledMfa &&
                result.json.twoFactorAuthenticationType !== TwoFactorAuthenticationType.withinMfaGracePeriod
            ) {
                await this.setState({
                    fullName: result.json.fullName,
                    mfaType: result.json.twoFactorAuthenticationType,
                    step: 'totp',
                });
            } else {
                const sessionResult = await fetchJson('/api/session', 'POST', item, [
                    ValidationErrorCode.WRONG_TOTP_TOKEN,
                ]);
                if (practitionerHasMfaDeactivated) {
                    this.rememberUser(sessionResult.json.user);
                    await this.setState({
                        mfaVerificationSwitchedOffOn: result.json.mfaVerificationSwitchedOffOn,
                        practitionerId: result.json.practitionerId,
                        redirectUrl: sessionResult.json.redirectUrl ?? null,
                        step: 'temporarilyDisabledMfa',
                        user: sessionResult.json.user,
                    });
                } else if (
                    result.json.twoFactorAuthenticationType !== TwoFactorAuthenticationType.withinMfaGracePeriod
                ) {
                    const startPage = sessionResult.json.redirectUrl || this.getUrlFromLocation() || '/home';
                    this.props.router.navigate('/setMfa?redirectTo=' + startPage);
                } else {
                    this.loadAndRedirectUser(sessionResult.json.user, sessionResult.json.redirectUrl);
                }
            }
        } catch (e: any) {
            await this.setState((prevState: ILoginState) => ({
                errorCode: e.errorCode,
                login: { ...prevState.login, password: '' },
            }));
        } finally {
            // Enable login button again after response
            await this.setState({ isLoginButtonDisabled: false });
        }
    }

    private goToStartpage(redirectUrl?: string) {
        if (redirectUrl) {
            window.location.href = redirectUrl;
        } else if (this.props.router.location) {
            const url = this.getUrlFromLocation();
            if (url) {
                this.props.router.navigate(url);
            }
        } else {
            this.props.router.navigate('/home');
        }
    }

    private getUrlFromLocation() {
        const location = this.props.router.location;
        if (location) {
            return `${location.pathname}${location.search ? location.search : ''}`;
        } else {
            return undefined;
        }
    }

    private setWarningMessageTimeout() {
        clearTimeout(this.warningMessageTimeout);
        this.warningMessageTimeout = window.setTimeout(
            async () => await this.showWarningMessage(),
            timeOutWarningMessageMilliseconds
        );
    }

    private setInactivityModalTimeout() {
        clearTimeout(this.inactivityModalTimeout);
        this.inactivityModalTimeout = window.setTimeout(
            async () => await this.setState({ showInactivityModal: true }),
            timeOutInactivityModalMilliseconds
        );
    }

    private async showWarningMessage() {
        await this.setState({ showWarningMessage: true });
        clearTimeout(this.warningMessageTimeout);
        this.setInactivityModalTimeout();
    }

    private async closeInactivityModal() {
        clearTimeout(this.warningMessageTimeout);
        clearTimeout(this.inactivityModalTimeout);
        await this.setState((prevState) => ({
            login: { ...prevState.login, password: '', totpToken: '' },
            showInactivityModal: false,
            showWarningMessage: false,
            step: 'credentials',
        }));
    }

    private showInactivityModal() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        key="loginAgain"
                        onClick={async () => await this.closeInactivityModal()}
                    >
                        {translateKey('login.loginAgain')}
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.closeInactivityModal()}
                size="small"
                title="Opnieuw proberen?"
            >
                <Text>
                    Je hebt te lang gewacht met het invullen van je verificatiecode. Om veiligheidsredenen vragen we je
                    om opnieuw in te loggen. Hulp nodig?{' '}
                    <Link
                        href="https://www.ksyos.nl/contact/faq"
                        openInNewTab={true}
                        variant="externalLink"
                    >
                        {' '}
                        Lees onze FAQ hier
                    </Link>
                    .
                </Text>
            </Modal>
        );
    }

    private async handleChangeToken(token: string) {
        clearTimeout(this.warningMessageTimeout);
        clearTimeout(this.inactivityModalTimeout);
        await this.setState({ showInactivityModal: false, showWarningMessage: false });

        await this.handleChange('totpToken', token);
        if (!this.state.showWarningMessage) {
            this.setWarningMessageTimeout();
        }

        const { errors, item } = validate(loginFullSchema, this.state.login);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        if (token.length === 6) {
            const result = await fetchJson('/api/session', 'POST', item, [
                ValidationErrorCode.WRONG_TOTP_TOKEN,
                ValidationErrorCode.WRONG_TOTP_TOKEN_WARNING,
                ValidationErrorCode.USER_BLOCKED_TEMPORARILY,
                ValidationErrorCode.RECENTLY_USED_TOTP_TOKEN,
            ]);

            if (
                [ValidationErrorCode.WRONG_TOTP_TOKEN, ValidationErrorCode.RECENTLY_USED_TOTP_TOKEN].includes(
                    result.errorCode as ValidationErrorCode
                )
            ) {
                await this.setState({ lockStatus: LockStatus.danger });

                setTimeout(async () => {
                    return await this.setState({
                        errors: emptyValidationErrors.set('totpToken', result.errorCode),
                        lockStatus: LockStatus.default,
                    });
                }, 2000);
            } else if (result.errorCode === ValidationErrorCode.WRONG_TOTP_TOKEN_WARNING) {
                await this.setState({
                    lockStatus: LockStatus.danger,
                    remainingAttempts: Number(result.message),
                    showWrongTokenWarningMessage: true,
                });
                setTimeout(async () => {
                    return await this.setState({
                        errors: emptyValidationErrors.set('totpToken', result.errorCode),
                        lockStatus: LockStatus.default,
                    });
                }, 2000);
            } else if (result.errorCode === ValidationErrorCode.USER_BLOCKED_TEMPORARILY) {
                clearTimeout(this.inactivityModalTimeout);

                return await this.setState({
                    errorCode: result.errorCode,
                    errors: emptyValidationErrors.set('totpToken', result.errorCode),
                    isFormDisabled: true,
                    lockStatus: LockStatus.danger,
                    login: { password: '', totpToken: '', userName: '' },
                    step: 'credentials',
                });
            } else if (result.status === 200) {
                await this.setState({ lockStatus: LockStatus.success });

                setTimeout(() => {
                    this.loadAndRedirectUser(result.json.user, result.json.redirectUrl);
                }, 2000);
            }
        }
    }

    private rememberUser(user: ISessionUser) {
        const newUser: IRememberedUser = {
            id: 0,
            initials: getDisplayInitials(user),
            lastLogin: new Date(),
            name: getDisplayName(user),
            username: user.userName,
        };

        if (this.state.rememberedUsers.length === 0) {
            localStorage.setItem('rememberedUsers', JSON.stringify([newUser]));
        } else if (
            this.state.rememberedUsers.findIndex((rememberedUser) => rememberedUser.username === newUser.username) !== 0
        ) {
            const rememberedUsers = this.state.rememberedUsers.filter(
                (rememberedUser) => rememberedUser.username !== newUser.username
            );
            rememberedUsers.forEach((rememberedUser, index) => (rememberedUser.id = index + 1));
            localStorage.setItem('rememberedUsers', JSON.stringify([newUser, ...rememberedUsers]));
        }
    }

    private removeUsersWithExpiredLastLoginDate(rememberedUsers: IRememberedUser[]): IRememberedUser[] {
        const rememberedUsersWithLastLoginDate = rememberedUsers.map((item) => {
            const lastLogin = item.lastLogin || new Date();

            return { ...item, lastLogin };
        });

        const filteredUsersWithNonExpiredLastLoginDate = this.filterOutUsersLastLoginDateSmallerThanFourWeeksAgo(
            rememberedUsersWithLastLoginDate
        );
        localStorage.setItem('rememberedUsers', JSON.stringify(filteredUsersWithNonExpiredLastLoginDate));

        return filteredUsersWithNonExpiredLastLoginDate;
    }

    private filterOutUsersLastLoginDateSmallerThanFourWeeksAgo(users: IRememberedUser[], weeks: number = 4) {
        const timeLimit = 604800000 * weeks; // weeks in ms
        const currentDay = new Date();

        return users.filter((rememberedUser) => {
            const lastLoginDate = new Date(rememberedUser.lastLogin);
            const timePassed = +currentDay - +lastLoginDate;
            const isExpired = timeLimit <= timePassed;

            return !isExpired;
        });
    }

    private loadAndRedirectUser(user: ISessionUser, redirectUrl: string) {
        this.rememberUser(user);
        this.props.dispatch(receiveLogin(user));
        this.goToStartpage(redirectUrl);
    }
}

export const NewLogin = withRouter(connect()(InnerNewLogin));
